import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import { getOauthUrl } from "@/api/integration"
import { MailboxProvider, selfConnectMailbox } from "@/api/mailbox_invite"
import { Button } from "@/components/ui/button"
import { OAuthRedirectModal } from "@/setup/connect/oauth-redirect-modal"
import { OauthProvider } from "@/setup/connect/auth_type"

interface ReconnectOauthButtonProps {
  provider: OauthProvider
  displayName: string
  returnPath: string
  /**
   * The mailbox this connection syncs, for Gmail/Microsoft. Reconnecting then goes through a
   * connect link pinned to it, so signing in with the wrong account re-authorizes nothing instead
   * of quietly opening a second connection to somebody's personal inbox.
   */
  mailbox?: string
  /**
   * True when this Microsoft connection reads a shared mailbox through a delegate. Those mailboxes
   * usually can't sign in themselves, so the account picker must not be pre-filled with one.
   */
  sharedMailbox?: boolean
  className?: string
}

function is_mailbox_provider(provider: OauthProvider): provider is MailboxProvider {
  return provider === "gmail" || provider === "microsoft"
}

export function ReconnectOauthButton({
  provider,
  displayName,
  returnPath,
  mailbox,
  sharedMailbox,
  className,
}: ReconnectOauthButtonProps) {
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showRedirect, setShowRedirect] = useState(false)

  // A mailbox reconnect goes straight to the provider on click: it needs no shareable link, and
  // minting one per click would leave a trail of live URLs nobody asked for.
  const pinned_to_mailbox = is_mailbox_provider(provider) && !!mailbox

  useAsyncEffect(async () => {
    if (pinned_to_mailbox) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const res = await getOauthUrl(provider, returnPath)
    setLoading(false)
    if (!res.ok) {
      setError(`Failed to start ${displayName} authorization. Please try again.`)
      return
    }
    setRedirectUrl(res.value.data)
  }, [provider, returnPath, pinned_to_mailbox])

  async function handleReconnect() {
    if (!pinned_to_mailbox) {
      if (!redirectUrl) return
      setShowRedirect(true)
      return
    }
    setLoading(true)
    setError(null)
    const started = await selfConnectMailbox({
      integration_name: provider as MailboxProvider,
      email: mailbox!,
      mode: sharedMailbox ? "delegated" : "direct",
      next: returnPath,
    })
    setLoading(false)
    if (!started.ok) {
      setError(started.error.message)
      return
    }
    setRedirectUrl(started.value.data)
    setShowRedirect(true)
  }

  return (
    <div class={className}>
      {error && <p class="text-sm text-red-600 mb-3">{error}</p>}
      {pinned_to_mailbox && (
        <p class="text-sm text-gray-500 mb-3">
          Reconnects <span class="font-mono text-xs">{mailbox}</span>.{" "}
          {sharedMailbox
            ? "Sign in with an account that has Full Access to it — we check that access before reconnecting."
            : "Signing in with a different account won't connect it."}
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        onClick={handleReconnect}
        disabled={loading || (!pinned_to_mailbox && !redirectUrl)}>
        {loading ? "Loading..." : `Reconnect ${displayName}`}
      </Button>
      {showRedirect && redirectUrl && <OAuthRedirectModal name={displayName} redirectUrl={redirectUrl} />}
    </div>
  )
}
